import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Calendar, Trash2, ExternalLink, Copy, QrCode, Download, Clock, Edit, X, ChevronLeft, ChevronRight, Layers, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper for translated calendar messages
const getCalMsg = (language, key) => {
  const msgs = {
    errorLoadingCalendars: { en: "Error loading calendars", es: "Error al cargar los calendarios", pt: "Erro ao carregar calendários", ht: "Erè nan chajman kalandriye" },
    enterCalendarName: { en: "Enter a calendar name", es: "Ingresa un nombre para el calendario", pt: "Digite um nome para o calendário", ht: "Antre yon non pou kalandriye a" },
    calendarCreated: { en: "Calendar created successfully", es: "Calendario creado exitosamente", pt: "Calendário criado com sucesso", ht: "Kalandriye kreye avèk siksè" },
    calendarUpdated: { en: "Calendar updated", es: "Calendario actualizado", pt: "Calendário atualizado", ht: "Kalandriye mete ajou" },
    errorUpdatingCalendar: { en: "Error updating calendar", es: "Error al actualizar el calendario", pt: "Erro ao atualizar calendário", ht: "Erè nan mete ajou kalandriye" },
    calendarDeleted: { en: "Calendar deleted", es: "Calendario eliminado", pt: "Calendário excluído", ht: "Kalandriye efase" },
    errorDeletingCalendar: { en: "Error deleting calendar", es: "Error al eliminar el calendario", pt: "Erro ao excluir calendário", ht: "Erè nan efase kalandriye" },
    selectContent: { en: "Select content", es: "Selecciona un contenido", pt: "Selecione um conteúdo", ht: "Chwazi yon kontni" },
    endTimeMustBeLater: { en: "End time must be after start time", es: "La hora de fin debe ser posterior a la hora de inicio", pt: "O horário de fim deve ser após o horário de início", ht: "Lè fen an dwe apre lè kòmansman an" },
    exceptionAlreadyExists: { en: "Exception already exists for this date", es: "Ya existe una excepción para esta fecha", pt: "Já existe uma exceção para esta data", ht: "Eksepsyon deja egziste pou dat sa a" },
    qrDownloaded: { en: "QR downloaded", es: "QR descargado", pt: "QR baixado", ht: "QR telechaje" },
    linkCopied: { en: "Link copied", es: "Enlace copiado", pt: "Link copiado", ht: "Lyen kopye" },
    // UI Elements
    calendarsTitle: { en: "Scheduling Calendars", es: "Calendarios de Programación", pt: "Calendários de Programação", ht: "Kalandriye Pwogramasyon" },
    calendarsDesc: { en: "Schedule which menu or slideshow to show by day and time", es: "Programa qué menú o slideshow mostrar según día y hora", pt: "Programe qual menu ou slideshow mostrar por dia e hora", ht: "Pwograme ki meni oswa slideshow pou montre pa jou ak lè" },
    createCalendar: { en: "Create Scheduling Calendar", es: "Crear Calendario de Programación", pt: "Criar Calendário de Programação", ht: "Kreye Kalandriye Pwogramasyon" },
    calendarName: { en: "Calendar Name", es: "Nombre del Calendario", pt: "Nome do Calendário", ht: "Non Kalandriye a" },
    calendarNamePlaceholder: { en: "Main Schedule...", es: "Programación Principal...", pt: "Programação Principal...", ht: "Pwogramasyon Prensipal..." },
    descriptionOptional: { en: "Description (optional)", es: "Descripción (opcional)", pt: "Descrição (opcional)", ht: "Deskripsyon (opsyonèl)" },
    descriptionPlaceholder: { en: "Restaurant schedule...", es: "Horario del restaurante...", pt: "Horário do restaurante...", ht: "Orè restoran an..." },
    timezone: { en: "Time Zone", es: "Zona Horaria", pt: "Fuso Horário", ht: "Zòn Lè" },
    timezoneApplied: { en: "Schedules will be applied according to this zone", es: "Los horarios se aplicarán según esta zona", pt: "Os horários serão aplicados de acordo com este fuso", ht: "Orè yo pral aplike selon zòn sa a" },
    defaultContent: { en: "Default Content", es: "Contenido por Defecto", pt: "Conteúdo Padrão", ht: "Kontni Pa Defo" },
    defaultContentDesc: { en: "Shows when there's no active schedule", es: "Se muestra cuando no hay programación activa", pt: "Exibido quando não há programação ativa", ht: "Afiche lè pa gen pwogramasyon aktif" },
    type: { en: "Type", es: "Tipo", pt: "Tipo", ht: "Tip" },
    noContent: { en: "No content", es: "Sin contenido", pt: "Sem conteúdo", ht: "San kontni" },
    menu: { en: "Menu", es: "Menú", pt: "Menu", ht: "Meni" },
    slideshow: { en: "Slideshow", es: "Slideshow", pt: "Slideshow", ht: "Slideshow" },
    weeklySchedule: { en: "Weekly Schedule", es: "Programación Semanal", pt: "Programação Semanal", ht: "Pwogramasyon Semèn" },
    cancel: { en: "Cancel", es: "Cancelar", pt: "Cancelar", ht: "Anile" },
    create: { en: "Create", es: "Crear", pt: "Criar", ht: "Kreye" },
    creating: { en: "Creating...", es: "Creando...", pt: "Criando...", ht: "Kreyasyon..." },
    newCalendar: { en: "New Calendar", es: "Nuevo Calendario", pt: "Novo Calendário", ht: "Nouvo Kalandriye" },
    editCalendar: { en: "Edit Calendar", es: "Editar Calendario", pt: "Editar Calendário", ht: "Modifye Kalandriye" },
    confirmTimezone: { en: "Confirm Time Zone", es: "Confirmar Zona Horaria", pt: "Confirmar Fuso Horário", ht: "Konfime Zòn Lè" },
    changeTimezone: { en: "Change Time Zone", es: "Cambiar Zona Horaria", pt: "Alterar Fuso Horário", ht: "Chanje Zòn Lè" },
    save: { en: "Save", es: "Guardar", pt: "Salvar", ht: "Anrejistre" },
    noCalendarsYet: { en: "You don't have calendars yet", es: "No tienes calendarios aún", pt: "Você ainda não tem calendários", ht: "Ou poko gen kalandriye" },
    createFirstCalendar: { en: "Create your first calendar to get started", es: "Crea tu primer calendario para comenzar", pt: "Crie seu primeiro calendário para começar", ht: "Kreye premye kalandriye ou pou kòmanse" },
    // Additional translations for calendar cards
    view: { en: "View", es: "Ver", pt: "Ver", ht: "Gade" },
    edit: { en: "Edit", es: "Editar", pt: "Editar", ht: "Modifye" },
    byDefault: { en: "Default:", es: "Por defecto:", pt: "Padrão:", ht: "Pa defo:" },
    weeklyBlocks: { en: "weekly blocks", es: "bloques semanales", pt: "blocos semanais", ht: "blòk semèn" },
    downloadQR: { en: "Download QR", es: "Descargar QR", pt: "Baixar QR", ht: "Telechaje QR" },
    editTimeBlock: { en: "Edit Time Block", es: "Editar Bloque de Tiempo", pt: "Editar Bloco de Tempo", ht: "Modifye Blòk Lè" },
    addTimeBlock: { en: "Add Time Block", es: "Agregar Bloque de Tiempo", pt: "Adicionar Bloco de Tempo", ht: "Ajoute Blòk Lè" },
    active: { en: "Active", es: "Activo", pt: "Ativo", ht: "Aktif" },
    add: { en: "Add", es: "Agregar", pt: "Adicionar", ht: "Ajoute" },
    menuNotFound: { en: "Menu not found", es: "Menú no encontrado", pt: "Menu não encontrado", ht: "Meni pa jwenn" },
  };
  return msgs[key]?.[language] || msgs[key]?.en || key;
};

// Common timezones for the Americas and other regions
const TIMEZONES = [
  { value: "America/Mexico_City", label: "Ciudad de México (GMT-6)", region: "México" },
  { value: "America/Cancun", label: "Cancún (GMT-5)", region: "México" },
  { value: "America/Tijuana", label: "Tijuana (GMT-8)", region: "México" },
  { value: "America/Monterrey", label: "Monterrey (GMT-6)", region: "México" },
  { value: "America/New_York", label: "Nueva York / Boston (GMT-5)", region: "Estados Unidos" },
  { value: "America/Chicago", label: "Chicago / Texas (GMT-6)", region: "Estados Unidos" },
  { value: "America/Denver", label: "Denver (GMT-7)", region: "Estados Unidos" },
  { value: "America/Los_Angeles", label: "Los Ángeles (GMT-8)", region: "Estados Unidos" },
  { value: "America/Phoenix", label: "Phoenix (GMT-7)", region: "Estados Unidos" },
  { value: "America/Bogota", label: "Bogotá (GMT-5)", region: "Colombia" },
  { value: "America/Lima", label: "Lima (GMT-5)", region: "Perú" },
  { value: "America/Santiago", label: "Santiago (GMT-4)", region: "Chile" },
  { value: "America/Buenos_Aires", label: "Buenos Aires (GMT-3)", region: "Argentina" },
  { value: "America/Sao_Paulo", label: "São Paulo (GMT-3)", region: "Brasil" },
  { value: "America/Caracas", label: "Caracas (GMT-4)", region: "Venezuela" },
  { value: "America/Guatemala", label: "Guatemala (GMT-6)", region: "Guatemala" },
  { value: "America/Panama", label: "Panamá (GMT-5)", region: "Panamá" },
  { value: "America/Costa_Rica", label: "Costa Rica (GMT-6)", region: "Costa Rica" },
  { value: "Europe/Madrid", label: "Madrid (GMT+1)", region: "España" },
  { value: "Europe/London", label: "Londres (GMT+0)", region: "Reino Unido" },
  { value: "UTC", label: "UTC (GMT+0)", region: "Universal" },
];

const DAYS_OF_WEEK = {
  en: [
    { key: 'monday', label: 'Monday', short: 'Mon' },
    { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
    { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
    { key: 'thursday', label: 'Thursday', short: 'Thu' },
    { key: 'friday', label: 'Friday', short: 'Fri' },
    { key: 'saturday', label: 'Saturday', short: 'Sat' },
    { key: 'sunday', label: 'Sunday', short: 'Sun' },
  ],
  es: [
    { key: 'monday', label: 'Lunes', short: 'Lun' },
    { key: 'tuesday', label: 'Martes', short: 'Mar' },
    { key: 'wednesday', label: 'Miércoles', short: 'Mié' },
    { key: 'thursday', label: 'Jueves', short: 'Jue' },
    { key: 'friday', label: 'Viernes', short: 'Vie' },
    { key: 'saturday', label: 'Sábado', short: 'Sáb' },
    { key: 'sunday', label: 'Domingo', short: 'Dom' },
  ],
  pt: [
    { key: 'monday', label: 'Segunda', short: 'Seg' },
    { key: 'tuesday', label: 'Terça', short: 'Ter' },
    { key: 'wednesday', label: 'Quarta', short: 'Qua' },
    { key: 'thursday', label: 'Quinta', short: 'Qui' },
    { key: 'friday', label: 'Sexta', short: 'Sex' },
    { key: 'saturday', label: 'Sábado', short: 'Sáb' },
    { key: 'sunday', label: 'Domingo', short: 'Dom' },
  ],
  ht: [
    { key: 'monday', label: 'Lendi', short: 'Len' },
    { key: 'tuesday', label: 'Madi', short: 'Mad' },
    { key: 'wednesday', label: 'Mèkredi', short: 'Mèk' },
    { key: 'thursday', label: 'Jedi', short: 'Jed' },
    { key: 'friday', label: 'Vandredi', short: 'Van' },
    { key: 'saturday', label: 'Samdi', short: 'Sam' },
    { key: 'sunday', label: 'Dimanch', short: 'Dim' },
  ],
};

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return `${hour}:00`;
});

const CalendarManager = ({ menus, slideshows, token, language = 'en' }) => {
  const [calendars, setCalendars] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [qrCalendar, setQrCalendar] = useState(null);
  const qrRef = useRef(null);

  // Timezone confirmation dialog state
  const [isTimezoneConfirmOpen, setIsTimezoneConfirmOpen] = useState(false);
  const [pendingCalendarAction, setPendingCalendarAction] = useState(null); // 'create' or 'update'
  const [detectedTimezone, setDetectedTimezone] = useState("America/Mexico_City");

  // Create form state
  const [newCalendarName, setNewCalendarName] = useState("");
  const [newCalendarDescription, setNewCalendarDescription] = useState("");
  const [defaultContentType, setDefaultContentType] = useState("none");
  const [defaultContentId, setDefaultContentId] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState("America/Mexico_City");

  // Weekly schedule editor state
  const [weeklySchedule, setWeeklySchedule] = useState({
    monday: [], tuesday: [], wednesday: [], thursday: [],
    friday: [], saturday: [], sunday: []
  });

  // Date exceptions state
  const [dateExceptions, setDateExceptions] = useState([]);
  const [selectedExceptionDate, setSelectedExceptionDate] = useState("");
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Slot editor state
  const [slotEditorOpen, setSlotEditorOpen] = useState(false);
  const [slotEditorTarget, setSlotEditorTarget] = useState(null); // { type: 'weekly'|'exception', day/dateIndex, slotIndex? }
  const [slotStartTime, setSlotStartTime] = useState("08:00");
  const [slotEndTime, setSlotEndTime] = useState("17:59");
  const [slotContentType, setSlotContentType] = useState("menu");
  const [slotContentId, setSlotContentId] = useState("");

  // Detect timezone on component mount
  useEffect(() => {
    detectTimezone();
  }, []);

  // Detect timezone from browser or IP
  const detectTimezone = async () => {
    try {
      // First try browser's timezone
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Check if it's in our list of supported timezones
      const isSupported = TIMEZONES.find(tz => tz.value === browserTimezone);
      
      if (isSupported) {
        setDetectedTimezone(browserTimezone);
        setSelectedTimezone(browserTimezone);
      } else {
        // Try to get timezone from IP using free API
        try {
          const response = await fetch('https://worldtimeapi.org/api/ip');
          const data = await response.json();
          if (data.timezone) {
            const ipTimezone = data.timezone;
            const isSupportedFromIP = TIMEZONES.find(tz => tz.value === ipTimezone);
            if (isSupportedFromIP) {
              setDetectedTimezone(ipTimezone);
              setSelectedTimezone(ipTimezone);
            }
          }
        } catch {
          // Could not detect timezone from IP, using default
        }
      }
    } catch {
      // Timezone detection failed, using default
    }
  };

  // Fetch calendars
  useEffect(() => {
    fetchCalendars();
    // fetchCalendars is a stable function defined in component scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCalendars = async () => {
    try {
      const response = await axios.get(`${API}/calendars`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendars(response.data);
    } catch (error) {
      console.error("Error fetching calendars:", error);
      toast.error(getCalMsg(language, 'errorLoadingCalendars'));
    } finally {
      setIsLoading(false);
    }
  };

  // Show timezone confirmation before creating
  const handleCreateCalendarClick = () => {
    if (!newCalendarName.trim()) {
      toast.error(getCalMsg(language, 'enterCalendarName'));
      return;
    }
    setPendingCalendarAction('create');
    setIsTimezoneConfirmOpen(true);
  };

  // Show timezone confirmation before updating
  const handleUpdateCalendarClick = () => {
    if (!editingCalendar) return;
    setPendingCalendarAction('update');
    setIsTimezoneConfirmOpen(true);
  };

  // Confirm and proceed with creation
  const handleConfirmTimezone = async () => {
    setIsTimezoneConfirmOpen(false);
    
    if (pendingCalendarAction === 'create') {
      await executeCreateCalendar();
    } else if (pendingCalendarAction === 'update') {
      await executeUpdateCalendar();
    }
    
    setPendingCalendarAction(null);
  };

  // Cancel timezone confirmation
  const handleCancelTimezoneConfirm = () => {
    setIsTimezoneConfirmOpen(false);
    setPendingCalendarAction(null);
  };

  const executeCreateCalendar = async () => {
    try {
      const payload = {
        name: newCalendarName,
        description: newCalendarDescription || null,
        default_content_type: defaultContentType === "none" ? null : defaultContentType,
        default_content_id: defaultContentId || null,
        weekly_schedule: weeklySchedule,
        date_exceptions: dateExceptions,
        timezone: selectedTimezone
      };

      const response = await axios.post(`${API}/calendars`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCalendars([...calendars, response.data]);
      resetCreateForm();
      setIsCreateOpen(false);
      toast.success(getCalMsg(language, 'calendarCreated'));
    } catch (error) {
      console.error("Error creating calendar:", error);
      const errorMsg = error.response?.data?.detail || "Error al crear el calendario";
      toast.error(errorMsg);
    }
  };

  const executeUpdateCalendar = async () => {
    if (!editingCalendar) return;

    try {
      const payload = {
        name: editingCalendar.name,
        description: editingCalendar.description,
        default_content_type: editingCalendar.default_content_type === "none" ? null : editingCalendar.default_content_type,
        default_content_id: editingCalendar.default_content_id || null,
        weekly_schedule: editingCalendar.weekly_schedule,
        date_exceptions: editingCalendar.date_exceptions,
        timezone: editingCalendar.timezone || "America/Mexico_City"
      };

      const response = await axios.put(`${API}/calendars/${editingCalendar.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCalendars(calendars.map(c => c.id === response.data.id ? response.data : c));
      setIsEditOpen(false);
      setEditingCalendar(null);
      toast.success(getCalMsg(language, 'calendarUpdated'));
    } catch (error) {
      console.error("Error updating calendar:", error);
      toast.error(getCalMsg(language, 'errorUpdatingCalendar'));
    }
  };

  const handleDeleteCalendar = async (calendarId) => {
    const confirmMsg = {
      en: "Are you sure you want to delete this calendar?",
      es: "¿Estás seguro de eliminar este calendario?",
      pt: "Tem certeza que deseja excluir este calendário?",
      ht: "Ou sèten ou vle efase kalandriye sa a?"
    };
    if (!window.confirm(confirmMsg[language] || confirmMsg.en)) return;

    try {
      await axios.delete(`${API}/calendars/${calendarId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendars(calendars.filter(c => c.id !== calendarId));
      toast.success(getCalMsg(language, 'calendarDeleted'));
    } catch (error) {
      console.error("Error deleting calendar:", error);
      toast.error(getCalMsg(language, 'errorDeletingCalendar'));
    }
  };

  const resetCreateForm = () => {
    setNewCalendarName("");
    setNewCalendarDescription("");
    setDefaultContentType("none");
    setDefaultContentId("");
    setSelectedTimezone("America/Mexico_City");
    setWeeklySchedule({
      monday: [], tuesday: [], wednesday: [], thursday: [],
      friday: [], saturday: [], sunday: []
    });
    setDateExceptions([]);
  };

  // Get timezone label for display
  const getTimezoneLabel = (tzValue) => {
    const tz = TIMEZONES.find(t => t.value === tzValue);
    return tz ? tz.label : tzValue;
  };

  const getPublicUrl = (calendarId) => `${window.location.origin}/calendar/${calendarId}`;

  const getContentName = (contentType, contentId) => {
    if (contentType === "menu") {
      const menu = menus.find(m => m.id === contentId);
      return menu?.name || getCalMsg(language, 'menuNotFound');
    } else if (contentType === "slideshow") {
      const slideshow = slideshows.find(s => s.id === contentId);
      return slideshow?.name || (language === 'en' ? 'Slideshow not found' : language === 'pt' ? 'Slideshow não encontrado' : language === 'ht' ? 'Slideshow pa jwenn' : 'Slideshow no encontrado');
    }
    return getCalMsg(language, 'noContent');
  };

  // Slot management functions
  const openSlotEditor = (target, existingSlot = null) => {
    setSlotEditorTarget(target);
    if (existingSlot) {
      setSlotStartTime(existingSlot.start_time);
      setSlotEndTime(existingSlot.end_time || "23:59");
      setSlotContentType(existingSlot.content_type);
      setSlotContentId(existingSlot.content_id);
    } else {
      setSlotStartTime("00:00");
      setSlotEndTime("23:59");
      setSlotContentType("menu");
      setSlotContentId(menus[0]?.id || "");
    }
    setSlotEditorOpen(true);
  };

  // Check if two time slots overlap
  const slotsOverlap = (slot1, slot2) => {
    return slot1.start_time < slot2.end_time && slot1.end_time > slot2.start_time;
  };

  // Check for overlapping slots in a list
  const checkOverlap = (slots, newSlot, excludeIndex = -1) => {
    for (let i = 0; i < slots.length; i++) {
      if (i === excludeIndex) continue; // Skip the slot being edited
      if (slotsOverlap(slots[i], newSlot)) {
        return {
          overlaps: true,
          conflictSlot: slots[i]
        };
      }
    }
    return { overlaps: false };
  };

  const saveSlot = () => {
    if (!slotContentId) {
      toast.error(getCalMsg(language, 'selectContent'));
      return;
    }

    // Validate that end time is after start time
    if (slotEndTime < slotStartTime) {
      toast.error(getCalMsg(language, 'endTimeMustBeLater'));
      return;
    }

    const newSlot = {
      start_time: slotStartTime,
      end_time: slotEndTime,
      content_type: slotContentType,
      content_id: slotContentId
    };

    const isEditing = editingCalendar !== null;
    
    if (slotEditorTarget.type === 'weekly') {
      const day = slotEditorTarget.day;
      const schedule = isEditing ? { ...editingCalendar.weekly_schedule } : { ...weeklySchedule };
      const existingSlots = schedule[day] || [];
      
      // Check for overlapping slots
      const excludeIndex = slotEditorTarget.slotIndex !== undefined ? slotEditorTarget.slotIndex : -1;
      const overlapCheck = checkOverlap(existingSlots, newSlot, excludeIndex);
      
      if (overlapCheck.overlaps) {
        toast.error(`Este horario se solapa con otro bloque existente (${overlapCheck.conflictSlot.start_time} - ${overlapCheck.conflictSlot.end_time})`);
        return;
      }
      
      if (slotEditorTarget.slotIndex !== undefined) {
        // Editing existing slot
        schedule[day] = schedule[day].map((s, i) => i === slotEditorTarget.slotIndex ? newSlot : s);
      } else {
        // Adding new slot
        schedule[day] = [...(schedule[day] || []), newSlot].sort((a, b) => a.start_time.localeCompare(b.start_time));
      }

      if (isEditing) {
        setEditingCalendar({ ...editingCalendar, weekly_schedule: schedule });
      } else {
        setWeeklySchedule(schedule);
      }
    } else if (slotEditorTarget.type === 'exception') {
      const exceptions = isEditing ? [...editingCalendar.date_exceptions] : [...dateExceptions];
      const excIndex = slotEditorTarget.dateIndex;
      const existingSlots = exceptions[excIndex].time_slots || [];
      
      // Check for overlapping slots
      const excludeIndex = slotEditorTarget.slotIndex !== undefined ? slotEditorTarget.slotIndex : -1;
      const overlapCheck = checkOverlap(existingSlots, newSlot, excludeIndex);
      
      if (overlapCheck.overlaps) {
        toast.error(`Este horario se solapa con otro bloque existente (${overlapCheck.conflictSlot.start_time} - ${overlapCheck.conflictSlot.end_time})`);
        return;
      }
      
      if (slotEditorTarget.slotIndex !== undefined) {
        exceptions[excIndex].time_slots = exceptions[excIndex].time_slots.map((s, i) => 
          i === slotEditorTarget.slotIndex ? newSlot : s
        );
      } else {
        exceptions[excIndex].time_slots = [...exceptions[excIndex].time_slots, newSlot].sort((a, b) => 
          a.start_time.localeCompare(b.start_time)
        );
      }

      if (isEditing) {
        setEditingCalendar({ ...editingCalendar, date_exceptions: exceptions });
      } else {
        setDateExceptions(exceptions);
      }
    }

    setSlotEditorOpen(false);
  };

  const deleteSlot = (target) => {
    const isEditing = editingCalendar !== null;

    if (target.type === 'weekly') {
      const day = target.day;
      const schedule = isEditing ? { ...editingCalendar.weekly_schedule } : { ...weeklySchedule };
      schedule[day] = schedule[day].filter((_, i) => i !== target.slotIndex);

      if (isEditing) {
        setEditingCalendar({ ...editingCalendar, weekly_schedule: schedule });
      } else {
        setWeeklySchedule(schedule);
      }
    } else if (target.type === 'exception') {
      const exceptions = isEditing ? [...editingCalendar.date_exceptions] : [...dateExceptions];
      exceptions[target.dateIndex].time_slots = exceptions[target.dateIndex].time_slots.filter((_, i) => 
        i !== target.slotIndex
      );

      if (isEditing) {
        setEditingCalendar({ ...editingCalendar, date_exceptions: exceptions });
      } else {
        setDateExceptions(exceptions);
      }
    }
  };

  // Date exception management
  const addDateException = (date) => {
    const isEditing = editingCalendar !== null;
    const exceptions = isEditing ? [...editingCalendar.date_exceptions] : [...dateExceptions];
    
    if (exceptions.find(e => e.date === date)) {
      toast.error(getCalMsg(language, 'exceptionAlreadyExists'));
      return;
    }

    const newException = {
      date,
      time_slots: [],
      is_disabled: false
    };

    if (isEditing) {
      setEditingCalendar({ ...editingCalendar, date_exceptions: [...exceptions, newException] });
    } else {
      setDateExceptions([...exceptions, newException]);
    }
    setSelectedExceptionDate("");
  };

  const removeDateException = (date) => {
    const isEditing = editingCalendar !== null;
    const exceptions = isEditing ? editingCalendar.date_exceptions : dateExceptions;
    const filtered = exceptions.filter(e => e.date !== date);

    if (isEditing) {
      setEditingCalendar({ ...editingCalendar, date_exceptions: filtered });
    } else {
      setDateExceptions(filtered);
    }
  };

  // Calendar navigation for date picker
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty slots for days before first day of month
    for (let i = 0; i < (firstDay.getDay() || 7) - 1; i++) {
      days.push(null);
    }
    
    // Add days of month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const downloadQR = () => {
    if (!qrRef.current || !qrCalendar) return;
    
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    
    img.onload = () => {
      canvas.width = 300;
      canvas.height = 300;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 300, 300);
      ctx.drawImage(img, 0, 0, 300, 300);
      
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `qr-calendario-${qrCalendar.name.replace(/\s+/g, '-').toLowerCase()}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      toast.success(getCalMsg(language, 'qrDownloaded'));
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Weekly Schedule Editor Component
  const WeeklyScheduleEditor = ({ schedule, isEditing }) => (
    <div className="space-y-3">
      <Label className="flex items-center gap-2 text-base">
        <Calendar className="w-4 h-4" /> {getCalMsg(language, 'weeklySchedule')}
      </Label>
      <div className="border rounded-lg overflow-hidden">
        <div className="grid grid-cols-7 bg-stone-100">
          {(DAYS_OF_WEEK[language] || DAYS_OF_WEEK.en).map(day => (
            <div key={day.key} className="p-2 text-center text-xs font-medium text-stone-600 border-r last:border-r-0">
              {day.short}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 min-h-[120px]">
          {(DAYS_OF_WEEK[language] || DAYS_OF_WEEK.en).map(day => {
            const daySlots = schedule?.[day.key] || [];
            return (
              <div key={day.key} className="border-r last:border-r-0 p-1 flex flex-col gap-1">
                {daySlots.map((slot, idx) => (
                  <div 
                    key={`${day.key}-${slot.start_time}-${idx}`}
                    className="text-xs p-1 rounded bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200 group relative"
                    onClick={() => openSlotEditor({ type: 'weekly', day: day.key, slotIndex: idx }, slot)}
                  >
                    <div className="font-medium">{slot.start_time} - {slot.end_time || '23:59'}</div>
                    <div className="truncate text-[10px]">
                      {slot.content_type === 'menu' ? '📋' : '🎬'} {getContentName(slot.content_type, slot.content_id)}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteSlot({ type: 'weekly', day: day.key, slotIndex: idx }); }}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] opacity-0 group-hover:opacity-100 flex items-center justify-center"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => openSlotEditor({ type: 'weekly', day: day.key })}
                  className="text-xs text-stone-400 hover:text-stone-600 p-1 rounded hover:bg-stone-100 flex items-center justify-center"
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Date Exceptions Editor Component
  const DateExceptionsEditor = ({ exceptions, isEditing }) => (
    <div className="space-y-3">
      <Label className="flex items-center gap-2 text-base">
        <Clock className="w-4 h-4" /> Excepciones por Fecha
      </Label>
      <p className="text-xs text-stone-500">
        Las excepciones reemplazan la programación semanal para fechas específicas
      </p>
      
      {/* Mini calendar for adding exceptions */}
      <div className="border rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-medium">
            {currentMonth.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs">
          {['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'].map(d => (
            <div key={d} className="text-stone-400 p-1">{d}</div>
          ))}
          {getDaysInMonth(currentMonth).map((date, idx) => {
            if (!date) return <div key={`empty-${idx}`} />;
            const dateStr = formatDate(date);
            const hasException = exceptions?.find(e => e.date === dateStr);
            const isToday = formatDate(new Date()) === dateStr;
            const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
            
            return (
              <button
                key={dateStr}
                onClick={() => !isPast && addDateException(dateStr)}
                disabled={isPast || hasException}
                className={`p-1 rounded text-xs ${
                  hasException ? 'bg-amber-200 text-amber-800 font-medium' :
                  isToday ? 'bg-blue-100 text-blue-800' :
                  isPast ? 'text-stone-300 cursor-not-allowed' :
                  'hover:bg-stone-100'
                }`}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>

      {/* List of exceptions */}
      {exceptions && exceptions.length > 0 && (
        <div className="space-y-2">
          {exceptions.map((exc, excIdx) => (
            <div key={exc.date} className="border rounded-lg p-3 bg-amber-50">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-sm">
                  {new Date(exc.date + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                </span>
                <Button variant="ghost" size="sm" onClick={() => removeDateException(exc.date)} className="h-6 w-6 p-0 text-red-500">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {exc.time_slots.map((slot, slotIdx) => (
                  <div 
                    key={slotIdx}
                    className="text-xs p-1 px-2 rounded bg-amber-200 text-amber-800 cursor-pointer hover:bg-amber-300 group relative"
                    onClick={() => openSlotEditor({ type: 'exception', dateIndex: excIdx, slotIndex: slotIdx }, slot)}
                  >
                    {slot.start_time} - {slot.end_time || '23:59'} | {slot.content_type === 'menu' ? '📋' : '🎬'} {getContentName(slot.content_type, slot.content_id)}
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteSlot({ type: 'exception', dateIndex: excIdx, slotIndex: slotIdx }); }}
                      className="ml-1 text-red-600 hover:text-red-800"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => openSlotEditor({ type: 'exception', dateIndex: excIdx })}
                  className="text-xs p-1 px-2 rounded bg-amber-100 text-amber-600 hover:bg-amber-200 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> {getCalMsg(language, 'add')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Default Content Selector Component
  const DefaultContentSelector = ({ contentType, setContentType, contentId, setContentId }) => (
    <div className="space-y-3">
      <Label className="flex items-center gap-2 text-base">
        {getCalMsg(language, 'defaultContent')}
      </Label>
      <p className="text-xs text-stone-500">
        {getCalMsg(language, 'defaultContentDesc')}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs">{getCalMsg(language, 'type')}</Label>
          <Select value={contentType || "none"} onValueChange={setContentType}>
            <SelectTrigger>
              <SelectValue placeholder={language === 'en' ? 'Select...' : language === 'pt' ? 'Selecionar...' : language === 'ht' ? 'Chwazi...' : 'Seleccionar...'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{getCalMsg(language, 'noContent')}</SelectItem>
              <SelectItem value="menu">{getCalMsg(language, 'menu')}</SelectItem>
              <SelectItem value="slideshow">{getCalMsg(language, 'slideshow')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {contentType && contentType !== "none" && (
          <div className="space-y-2">
            <Label className="text-xs">{language === 'en' ? 'Content' : language === 'pt' ? 'Conteúdo' : language === 'ht' ? 'Kontni' : 'Contenido'}</Label>
            <Select value={contentId || ""} onValueChange={setContentId}>
              <SelectTrigger>
                <SelectValue placeholder={language === 'en' ? 'Select...' : language === 'pt' ? 'Selecionar...' : language === 'ht' ? 'Chwazi...' : 'Seleccionar...'} />
              </SelectTrigger>
              <SelectContent>
                {contentType === "menu" && menus.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
                {contentType === "slideshow" && slideshows.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return <div className="text-center py-12 text-stone-500">{language === 'en' ? 'Loading calendars...' : language === 'pt' ? 'Carregando calendários...' : language === 'ht' ? 'Chajman kalandriye...' : 'Cargando calendarios...'}</div>;
  }

  return (
    <div className="space-y-6" data-testid="calendar-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-stone-900 flex items-center gap-2">
            <Calendar className="w-6 h-6" /> {getCalMsg(language, 'calendarsTitle')}
          </h2>
          <p className="text-stone-500 mt-1">
            {getCalMsg(language, 'calendarsDesc')}
          </p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={(open) => { setIsCreateOpen(open); if (!open) resetCreateForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-calendar-btn">
              <Plus className="w-4 h-4 mr-2" /> {getCalMsg(language, 'newCalendar')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{getCalMsg(language, 'createCalendar')}</DialogTitle>
              <DialogDescription>{language === 'en' ? 'Configure schedules to show different content' : language === 'pt' ? 'Configure horários para mostrar diferentes conteúdos' : language === 'ht' ? 'Konfigire orè pou montre diferan kontni' : 'Configura horarios para mostrar diferentes contenidos'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Basic info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{getCalMsg(language, 'calendarName')}</Label>
                  <Input
                    value={newCalendarName}
                    onChange={(e) => setNewCalendarName(e.target.value)}
                    placeholder={getCalMsg(language, 'calendarNamePlaceholder')}
                    data-testid="calendar-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{getCalMsg(language, 'descriptionOptional')}</Label>
                  <Input
                    value={newCalendarDescription}
                    onChange={(e) => setNewCalendarDescription(e.target.value)}
                    placeholder={getCalMsg(language, 'descriptionPlaceholder')}
                  />
                </div>
              </div>

              {/* Timezone selector */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-blue-600" />
                    <div>
                      <Label className="text-blue-900 font-medium">{getCalMsg(language, 'timezone')}</Label>
                      <p className="text-xs text-blue-600">{getCalMsg(language, 'timezoneApplied')}</p>
                    </div>
                  </div>
                  <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
                    <SelectTrigger className="w-[280px] bg-white" data-testid="timezone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>
                          <span className="flex items-center gap-2">
                            <span className="text-xs text-stone-400">{tz.region}:</span>
                            <span>{tz.label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Default Content */}
              <DefaultContentSelector 
                contentType={defaultContentType}
                setContentType={setDefaultContentType}
                contentId={defaultContentId}
                setContentId={setDefaultContentId}
              />

              {/* Weekly Schedule */}
              <WeeklyScheduleEditor schedule={weeklySchedule} isEditing={false} />

              {/* Date Exceptions */}
              <DateExceptionsEditor exceptions={dateExceptions} isEditing={false} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>{getCalMsg(language, 'cancel')}</Button>
              <Button onClick={handleCreateCalendarClick} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-create-calendar-btn">
                {getCalMsg(language, 'create')} {getCalMsg(language, 'calendarsTitle').split(' ')[0]}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Calendar list */}
      {calendars.length === 0 ? (
        <Card className="border-dashed border-stone-300">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto text-stone-300 mb-4" />
            <h3 className="text-lg font-medium text-stone-900 mb-2">
              {getCalMsg(language, 'noCalendarsYet')}
            </h3>
            <p className="text-stone-500 mb-4">
              {getCalMsg(language, 'createFirstCalendar')}
            </p>
            <Button onClick={() => setIsCreateOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" /> {getCalMsg(language, 'newCalendar')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {calendars.map((calendar) => (
            <Card key={calendar.id} className="border-stone-200 hover:shadow-md transition-shadow duration-200" data-testid={`calendar-card-${calendar.id}`}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-stone-900 flex items-center justify-between">
                  {calendar.name}
                  <span className="text-xs font-normal text-emerald-600 bg-emerald-50 px-2 py-1 rounded">
                    <Calendar className="w-3 h-3 inline mr-1" />
                    {getCalMsg(language, 'active')}
                  </span>
                </CardTitle>
                <CardDescription className="text-stone-500">
                  {calendar.description || (language === 'en' ? 'No description' : language === 'pt' ? 'Sem descrição' : language === 'ht' ? 'San deskripsyon' : 'Sin descripción')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stats */}
                <div className="text-xs text-stone-500 space-y-1">
                  <div className="flex items-center gap-2">
                    <Layers className="w-3 h-3" />
                    {getCalMsg(language, 'byDefault')} {calendar.default_content_type ? 
                      getContentName(calendar.default_content_type, calendar.default_content_id) : 
                      getCalMsg(language, 'noContent')
                    }
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    {Object.values(calendar.weekly_schedule || {}).flat().length} {getCalMsg(language, 'weeklyBlocks')}
                  </div>
                  {calendar.date_exceptions?.length > 0 && (
                    <div className="flex items-center gap-2 text-amber-600">
                      <Calendar className="w-3 h-3" />
                      {calendar.date_exceptions.length} {language === 'en' ? 'exception(s)' : language === 'pt' ? 'exceção(ões)' : language === 'ht' ? 'eksepsyon' : 'excepción(es)'}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-blue-600">
                    <Globe className="w-3 h-3" />
                    {getTimezoneLabel(calendar.timezone || "America/Mexico_City")}
                  </div>
                </div>

                {/* Public URL */}
                <div className="bg-stone-50 rounded-lg p-2">
                  <div className="flex items-center gap-2">
                    <Input 
                      value={getPublicUrl(calendar.id)} 
                      readOnly 
                      className="text-xs h-8 bg-white"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => {
                        navigator.clipboard.writeText(getPublicUrl(calendar.id));
                        toast.success(getCalMsg(language, 'linkCopied'));
                      }}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => window.open(getPublicUrl(calendar.id), '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-1" /> {getCalMsg(language, 'view')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setQrCalendar(calendar)}
                  >
                    <QrCode className="w-4 h-4 mr-1" /> QR
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      setEditingCalendar({ ...calendar });
                      setIsEditOpen(true);
                    }}
                  >
                    <Edit className="w-4 h-4 mr-1" /> {getCalMsg(language, 'edit')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-500 hover:text-red-600"
                    onClick={() => handleDeleteCalendar(calendar.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Slot Editor Dialog */}
      <Dialog open={slotEditorOpen} onOpenChange={setSlotEditorOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{slotEditorTarget?.slotIndex !== undefined ? getCalMsg(language, 'editTimeBlock') : getCalMsg(language, 'addTimeBlock')}</DialogTitle>
            <DialogDescription>{language === 'en' ? 'Configure what content to show and when' : language === 'pt' ? 'Configure qual conteúdo mostrar e quando' : language === 'ht' ? 'Konfigire ki kontni pou montre e kilè' : 'Configura qué contenido mostrar y en qué horario'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'en' ? 'Start time' : language === 'pt' ? 'Hora de início' : language === 'ht' ? 'Lè kòmanse' : 'Hora de inicio'}</Label>
                <Select value={slotStartTime} onValueChange={setSlotStartTime}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map(h => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{language === 'en' ? 'End time' : language === 'pt' ? 'Hora de fim' : language === 'ht' ? 'Lè fini' : 'Hora de fin'}</Label>
                <Select value={slotEndTime} onValueChange={setSlotEndTime}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map(h => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                    <SelectItem value="23:59">23:59</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-xs text-stone-500 bg-stone-100 p-2 rounded">
              {language === 'en' ? 'Example: 00:00 - 17:59 (Breakfast/Lunch), 18:00 - 23:59 (Dinner)' : language === 'pt' ? 'Exemplo: 00:00 - 17:59 (Café/Almoço), 18:00 - 23:59 (Jantar)' : language === 'ht' ? 'Egzanp: 00:00 - 17:59 (Dejene/Manje midi), 18:00 - 23:59 (Dine)' : 'Ejemplo: 00:00 - 17:59 (Desayuno/Almuerzo), 18:00 - 23:59 (Cena)'}
            </div>
            <div className="space-y-2">
              <Label>{language === 'en' ? 'Content type' : language === 'pt' ? 'Tipo de conteúdo' : language === 'ht' ? 'Tip kontni' : 'Tipo de contenido'}</Label>
              <Select value={slotContentType} onValueChange={(v) => { setSlotContentType(v); setSlotContentId(""); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="menu">{getCalMsg(language, 'menu')}</SelectItem>
                  <SelectItem value="slideshow">{getCalMsg(language, 'slideshow')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{language === 'en' ? 'Content' : language === 'pt' ? 'Conteúdo' : language === 'ht' ? 'Kontni' : 'Contenido'}</Label>
              <Select value={slotContentId} onValueChange={setSlotContentId}>
                <SelectTrigger>
                  <SelectValue placeholder={language === 'en' ? 'Select...' : language === 'pt' ? 'Selecionar...' : language === 'ht' ? 'Chwazi...' : 'Seleccionar...'} />
                </SelectTrigger>
                <SelectContent>
                  {slotContentType === "menu" && menus.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                  {slotContentType === "slideshow" && slideshows.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSlotEditorOpen(false)}>{getCalMsg(language, 'cancel')}</Button>
            <Button onClick={saveSlot} className="bg-emerald-600 hover:bg-emerald-700">{getCalMsg(language, 'save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Calendar Dialog */}
      <Dialog open={isEditOpen} onOpenChange={(open) => { setIsEditOpen(open); if (!open) setEditingCalendar(null); }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{getCalMsg(language, 'editCalendar')}</DialogTitle>
            <DialogDescription>{language === 'en' ? 'Modify the calendar configuration' : language === 'pt' ? 'Modifique a configuração do calendário' : language === 'ht' ? 'Modifye konfigirasyon kalandriye a' : 'Modifica la configuración del calendario'}</DialogDescription>
          </DialogHeader>
          {editingCalendar && (
            <div className="space-y-6 py-4">
              {/* Basic info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{getCalMsg(language, 'calendarName')}</Label>
                  <Input
                    value={editingCalendar.name}
                    onChange={(e) => setEditingCalendar({ ...editingCalendar, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{getCalMsg(language, 'descriptionOptional')}</Label>
                  <Input
                    value={editingCalendar.description || ""}
                    onChange={(e) => setEditingCalendar({ ...editingCalendar, description: e.target.value })}
                  />
                </div>
              </div>

              {/* Timezone selector */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-blue-600" />
                    <div>
                      <Label className="text-blue-900 font-medium">{getCalMsg(language, 'timezone')}</Label>
                      <p className="text-xs text-blue-600">{getCalMsg(language, 'timezoneApplied')}</p>
                    </div>
                  </div>
                  <Select 
                    value={editingCalendar.timezone || "America/Mexico_City"} 
                    onValueChange={(v) => setEditingCalendar({ ...editingCalendar, timezone: v })}
                  >
                    <SelectTrigger className="w-[280px] bg-white" data-testid="edit-timezone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>
                          <span className="flex items-center gap-2">
                            <span className="text-xs text-stone-400">{tz.region}:</span>
                            <span>{tz.label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Default Content */}
              <DefaultContentSelector 
                contentType={editingCalendar.default_content_type || "none"}
                setContentType={(v) => setEditingCalendar({ ...editingCalendar, default_content_type: v })}
                contentId={editingCalendar.default_content_id || ""}
                setContentId={(v) => setEditingCalendar({ ...editingCalendar, default_content_id: v })}
              />

              {/* Weekly Schedule */}
              <WeeklyScheduleEditor schedule={editingCalendar.weekly_schedule} isEditing={true} />

              {/* Date Exceptions */}
              <DateExceptionsEditor exceptions={editingCalendar.date_exceptions} isEditing={true} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>{getCalMsg(language, 'cancel')}</Button>
            <Button onClick={handleUpdateCalendarClick} className="bg-emerald-600 hover:bg-emerald-700">
              {language === 'en' ? 'Save Changes' : language === 'pt' ? 'Salvar Alterações' : language === 'ht' ? 'Anrejistre Chanjman' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timezone Confirmation Dialog */}
      <Dialog open={isTimezoneConfirmOpen} onOpenChange={setIsTimezoneConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              {getCalMsg(language, 'confirmTimezone')}
            </DialogTitle>
            <DialogDescription>
              {language === 'en' ? 'Before saving, confirm that the timezone is correct' : language === 'pt' ? 'Antes de salvar, confirme que o fuso horário está correto' : language === 'ht' ? 'Anvan anrejistre, konfime zòn lè a kòrèk' : 'Antes de guardar, confirma que la zona horaria es correcta'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <div className="text-sm text-blue-600 mb-2">{language === 'en' ? 'Selected timezone:' : language === 'pt' ? 'Fuso horário selecionado:' : language === 'ht' ? 'Zòn lè chwazi:' : 'Zona horaria seleccionada:'}</div>
              <div className="text-xl font-semibold text-blue-900">
                {getTimezoneLabel(pendingCalendarAction === 'create' ? selectedTimezone : (editingCalendar?.timezone || "America/Mexico_City"))}
              </div>
              <div className="text-xs text-blue-500 mt-2">
                {language === 'en' ? 'Scheduled times will be applied according to this timezone' : language === 'pt' ? 'Os horários programados serão aplicados de acordo com este fuso' : language === 'ht' ? 'Orè pwograme yo pral aplike selon zòn lè sa a' : 'Los horarios programados se aplicarán según esta zona horaria'}
              </div>
            </div>
            
            {detectedTimezone !== (pendingCalendarAction === 'create' ? selectedTimezone : (editingCalendar?.timezone || "America/Mexico_City")) && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 text-amber-700 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{language === 'en' ? 'Your detected timezone is:' : language === 'pt' ? 'Seu fuso horário detectado é:' : language === 'ht' ? 'Zòn lè ou detekte se:' : 'Tu zona horaria detectada es:'} <strong>{getTimezoneLabel(detectedTimezone)}</strong></span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleCancelTimezoneConfirm} className="w-full sm:w-auto">
              {getCalMsg(language, 'changeTimezone')}
            </Button>
            <Button onClick={handleConfirmTimezone} className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700">
              {language === 'en' ? 'Confirm and Save' : language === 'pt' ? 'Confirmar e Salvar' : language === 'ht' ? 'Konfime epi Anrejistre' : 'Confirmar y Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={!!qrCalendar} onOpenChange={(open) => !open && setQrCalendar(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" /> QR - {qrCalendar?.name}
            </DialogTitle>
            <DialogDescription>{language === 'en' ? 'Scan to view the scheduled calendar' : language === 'pt' ? 'Escaneie para ver o calendário programado' : language === 'ht' ? 'Eskane pou wè kalandriye pwograme a' : 'Escanea para ver el calendario programado'}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <div ref={qrRef} className="p-4 bg-white rounded-lg border shadow-sm">
              {qrCalendar && <QRCodeSVG value={getPublicUrl(qrCalendar.id)} size={200} level="H" includeMargin />}
            </div>
            <p className="text-xs text-stone-500 text-center break-all max-w-[250px]">
              {qrCalendar && getPublicUrl(qrCalendar.id)}
            </p>
          </div>
          <DialogFooter className="flex-row gap-2 sm:justify-center">
            <Button variant="outline" onClick={() => { if (qrCalendar) { navigator.clipboard.writeText(getPublicUrl(qrCalendar.id)); toast.success(getCalMsg(language, 'linkCopied')); } }} className="flex-1">
              <Copy className="w-4 h-4 mr-2" /> {language === 'en' ? 'Copy Link' : language === 'pt' ? 'Copiar Link' : language === 'ht' ? 'Kopye Lyen' : 'Copiar Link'}
            </Button>
            <Button onClick={downloadQR} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
              <Download className="w-4 h-4 mr-2" /> {getCalMsg(language, 'downloadQR')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarManager;
